import React, { useContext, useEffect, useState } from 'react';
import {
  TextField,
  Alert,
  Box,
  Button,
  Chip,
  Typography,
  IconButton,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import moment from 'moment/moment';

export default function PlanHeader({ auth, type, form, setForm, handleSubmit, categories = null }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editable, setEditable] = useState(false);
  let selectedItem = null;
  let selectedParent = null;
  let bgColor = 'grey.500';

  if (auth) {
    selectedItem = categories.find((item) =>
      form.sub_category_id !== null
        ? item.child_id === form.sub_category_id
        : item.parent_id === form.category_id && item.type === 'parent',
    );

    selectedParent =
      selectedItem?.type === 'parent'
        ? selectedItem
        : categories.find(
            (item) => item.type === 'parent' && item.parent_id === selectedItem?.parent_id,
          );

    bgColor =
      selectedParent?.name === 'Regular'
        ? 'success.main'
        : selectedParent?.name === 'Special'
        ? 'warning.main'
        : 'grey.500';
  }

  const handleChange = (e) => {
    const selectedId = e.target.value;

    const selectedItem = categories.find((item) => item.id === selectedId);

    if (!selectedItem) return;

    setForm((prev) => ({
      ...prev,
      category_id: selectedItem.parent_id,
      sub_category_id: selectedItem.type === 'child' ? selectedItem.child_id : null,
    }));
  };

  const handleStatusStyle = (value) => {
    switch (value) {
      case 1:
        return {
          color: 'default',
          sx: {},
        };

      case 2:
        return {
          color: 'warning',
          sx: {},
        };

      case 3:
        return {
          color: undefined,
          sx: {
            backgroundColor: '#FFDE21',
            color: '#000',
          },
        };

      case 4:
      case 5:
        return {
          color: 'success',
          sx: {},
        };

      default:
        return {
          color: 'default',
          sx: {},
        };
    }
  };

  useEffect(() => {
    const isEditable = form.status_id === 1;
    setEditable(isEditable);
  }, [form]);
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {editingTitle ? (
              <TextField
                value={form?.title}
                autoFocus
                variant='standard'
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                onBlur={() => setEditingTitle(false)}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '2rem',
                  },
                }}
              />
            ) : (
              <>
                <Typography variant='h4'>{form.title}</Typography>

                {type === 'pm' && editable ? (
                  <IconButton size='small' onClick={() => setEditingTitle(true)}>
                    <EditIcon fontSize='small' />
                  </IconButton>
                ) : (
                  ''
                )}
              </>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 1,
            }}
          >
            <Chip
              size='small'
              label={form.status}
              color={handleStatusStyle(form?.status_id)?.color}
              sx={handleStatusStyle(form?.status_id)?.sx}
            />
            {auth && editable ? (
              <FormControl size='small'>
                <Select
                  value={selectedItem?.id ?? ''}
                  onChange={handleChange}
                  renderValue={(selectedId) => {
                    const selected = categories.find((item) => item.id === selectedId);

                    if (!selected) return '';

                    if (selected.type === 'parent') {
                      return selected.name;
                    }

                    const parent = categories.find(
                      (item) => item.type === 'parent' && item.parent_id === selected.parent_id,
                    );

                    return `${parent?.name ?? ''} - ${selected.name}`;
                  }}
                  sx={{
                    minWidth: 220,
                    height: 28,
                    bgcolor: bgColor,
                    color: '#fff',
                    '& .MuiSelect-select': {
                      py: 0.5,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 0,
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#fff',
                    },
                  }}
                >
                  {categories
                    .filter((item) => item.type === 'parent')
                    .map((parent) => {
                      const childItems = categories.filter(
                        (item) => item.type === 'child' && item.parent_id === parent.parent_id,
                      );

                      return [
                        <MenuItem key={`parent-${parent.id}`} value={parent.id}>
                          {parent.name}
                        </MenuItem>,

                        ...childItems.map((child) => (
                          <MenuItem key={`child-${child.id}`} value={child.id} sx={{ pl: 4 }}>
                            {child.name}
                          </MenuItem>
                        )),
                      ];
                    })}
                </Select>
              </FormControl>
            ) : (
              <Chip
                size='small'
                color={form.category === 'Regular' ? 'success' : 'warning'}
                label={`${form.category}${
                  form.sub_category !== null ? ` - ${form.sub_category}` : ''
                } Sampling`}
              />
            )}
          </Box>

          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {form?.plan_id} • {moment(form?.created_at).format('MMM YYYY')} • Owner {form?.owner}
          </Typography>
        </Box>

        {editable && type === 'pm' ? (
          <Button
            variant='contained'
            startIcon={<SendIcon />}
            color='success'
            onClick={() => handleSubmit()}
            sx={{ height: '40px' }}
          >
            SUBMIT FOR APPROVAL
          </Button>
        ) : (
          ''
        )}
      </Box>
      {editable && type === 'pm' ? (
        <Alert
          severity='success'
          sx={{
            mb: 2,
            borderLeft: '4px solid',
            borderColor: 'success.main',
          }}
        >
          <strong>Product Manager view</strong> — You can edit requested quantities.
        </Alert>
      ) : (
        ''
      )}
    </>
  );
}
